import { Button, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

import useAuth from '@/hooks/useAuth';
import { getBrowserTranslations } from '@/utils/translations/Translations';

type UserLoadErrorProps = {
  b2cId: string | null;
  onRetry: () => void;
};

// Shown by UsersProvider when the signed-in user's record can't be loaded. Rendered outside the
// normal Page layout and above LocaleProvider (both depend on the user that failed to load), so the
// text follows the browser language rather than the user's saved preference.
export default function UserLoadError({ b2cId, onRetry }: UserLoadErrorProps) {
  const { logoutCurrentUser } = useAuth();
  const text = useMemo(() => getBrowserTranslations().userLoadError, []);
  const shownAt = useMemo(() => new Date().toISOString(), []);

  return (
    <Stack alignItems='center' justifyContent='center' minHeight='100vh' spacing={2} px={2}>
      <Typography variant='h5'>{text.title}</Typography>
      <Typography textAlign='center'>{text.message}</Typography>
      <Typography variant='body2' color='text.secondary'>
        {text.referenceLabel}: {b2cId ?? 'unknown'} · {shownAt}
      </Typography>
      <Stack direction='row' spacing={1}>
        <Button variant='primary' onClick={onRetry}>
          {text.tryAgain}
        </Button>
        <Button variant='secondary' onClick={() => void logoutCurrentUser('UserSignOut')}>
          {text.signOut}
        </Button>
        <Button
          variant='secondary'
          onClick={() =>
            window.open('https://recognition-help.arborday.org', '_blank', 'noopener,noreferrer')
          }
        >
          {text.contactUs}
        </Button>
      </Stack>
    </Stack>
  );
}
